import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { Stack } from '@/common/components/Stack/Stack';
import { MARKET } from '@/common/constants/markets';
import { ROUTES } from '@/common/constants/routes';
import { InsuranceOptOutModal } from '@/common/containers/Dashboard/Insurance/components/InsuranceOptOutModal/InsuranceOptOutModal';
import { InsuranceRemovalModal } from '@/common/containers/Dashboard/Insurance/components/InsuranceRemovalModal/InsuranceRemovalModal';
import { useMarket } from '@/common/hooks/useMarket';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import {
  IInsuranceMember,
  IInsurancePolicy,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { removeFalsey } from '@coverflex-tech/hypernova-utils';
import { useRouter } from 'expo-router';

export function useGetCoveredPersonActions(
  person: IInsuranceMember,
  policy: IInsurancePolicy
) {
  const { t } = useTranslation();
  const router = useRouter();
  const { slug } = useMarket();
  const [displayRemovalModal, toggleRemovalModal] = useState(false);

  const allowPaymentMethodChange =
    person.paymentType !== 'company' &&
    policy.holderPaymentMethodsAllowed.length > 1 &&
    slug === MARKET.PT;

  const policyMembers = person.type === 'employee' ? policy.persons : [];

  if (person.status !== 'active') {
    return null;
  }

  const actions = [
    allowPaymentMethodChange && (
      <Button
        key="change-payment"
        variant="secondary"
        onPress={() => {
          router.push({
            pathname: ROUTES.INSURANCE_FAMILY_MEMBER_PAYMENT,
            params: {
              policyId: policy.id,
              memberId: person.id,
            },
          });
        }}
      >
        {t('dashboard.insurance_detail.table_actions.change_payment')}
      </Button>
    ),
    isPersonRemovalAllowed(person, policy) && (
      <Fragment key="remove-person">
        {displayRemovalModal && (
          <>
            {person.optOutEligible ? (
              <InsuranceOptOutModal
                onClose={() => toggleRemovalModal(false)}
                peopleToRemove={
                  policyMembers.length > 0
                    ? policyMembers.filter(
                        (member) =>
                          member.status === 'pending' ||
                          member.status === 'active'
                      )
                    : [person]
                }
                policyId={policy.id}
                renewalDate={policy.renewsOn}
                companyName={policy.product.provider.name}
              />
            ) : (
              <InsuranceRemovalModal
                onClose={() => toggleRemovalModal(false)}
                onConfirm={() => {
                  AnalyticsService.openChat();
                }}
              />
            )}
          </>
        )}
        <Button
          variant="tertiary"
          size="sm"
          onPress={() => {
            toggleRemovalModal(true);
          }}
        >
          {t('dashboard.insurance_detail.table_actions.remove')}
        </Button>
      </Fragment>
    ),
  ].filter(removeFalsey);

  if (actions.length) {
    return <Stack gap="md">{actions}</Stack>;
  }

  return null;
}

export function isPersonRemovalAllowed(
  person: IInsuranceMember,
  policy: IInsurancePolicy
) {
  if (person.type === 'employee')
    return policy.optOutAllowed && person.optOutEligible;

  const canEmployeeOptOut =
    policy.persons.find((p) => p.type === 'employee')?.optOutEligible ?? false;

  return !policy.optOutAllowed || (policy.optOutAllowed && !canEmployeeOptOut);
}
